import { z } from 'zod';
import { PlanName } from './enums';
import { TenantSlugSchema } from './tenant';

/**
 * Form pointer published by the CMS to R2 so the Workers form receiver can
 * resolve `{tenantSlug, formSlug}` → notification settings without touching
 * the Platform DB or CMS at request time. Mirrors the route-pointer
 * pattern (Session 10) — a single R2 GET answers everything the receiver
 * needs at the edge.
 *
 * Stored at `r2://daigakus-builds/_forms/{tenantSlug}/{formSlug}.json`.
 * Slug-keyed because the Workers receiver only knows the slug at the
 * edge — the route pointer is consulted to resolve metadata, but the
 * route pointer's `tenantId` is the Platform DB UUID while the CMS
 * Forms hook lives in the CMS DB (numeric id), so the slug is the
 * single source of truth that both sides can agree on.
 *
 * The receiver MUST cross-validate `tenantId` against the value resolved
 * from the route pointer, since the URL slug → tenantId resolution lives
 * in a different file. A mismatched pointer is treated as a missing form.
 */
export const FormPointerSchema = z.object({
  version: z.literal(1),
  // Loosened from UUID-only: the CMS hook stamps the numeric Payload
  // tenant id (stringified) here while the canonical join key is now
  // the slug (see `formPointerKey`). Keeping the field for downstream
  // metadata + audit; receivers no longer cross-check it.
  tenantId: z.string().min(1),
  tenantSlug: TenantSlugSchema,
  formId: z.union([z.string().min(1), z.number().int().nonnegative()]),
  formSlug: z.string().min(1),
  /** Mirrors the `Forms.enabled` field at write time. Disabled forms 410. */
  enabled: z.boolean(),
  /** Mirrors `Forms.turnstile_required` at write time. */
  turnstileRequired: z.boolean(),
  /** Notification recipient list, snapshotted from `Forms.notify_emails`. */
  notifyEmails: z.array(z.string().email()).default([]),
  /**
   * Customer-facing automatic reply settings. Older pointers may omit this;
   * the receiver defaults to disabled so a stale pointer cannot be used as
   * a third-party mail relay by adding an arbitrary `email` field.
   */
  autoReply: z
    .object({
      enabled: z.boolean().default(true),
      subject: z.string().min(1).optional(),
      bodyText: z.string().min(1).optional(),
      emailFieldNames: z.array(z.string().min(1)).default(['email']),
    })
    .default({ enabled: false, emailFieldNames: [] }),
  /** Tenant plan at pointer write time — drives the receiver's notify cadence. */
  plan: z.nativeEnum(PlanName),
  /**
   * Plain-text or hidden-honeypot field name. The receiver treats any
   * submission with a non-empty value here as spam.
   */
  honeypotField: z.string().min(1).default('website'),
  /**
   * Substring matches (case-insensitive). Submissions matching any of
   * these are flagged `spam` instead of `received` but still persist —
   * audit + manual review remains possible.
   */
  bannedKeywords: z.array(z.string().min(1)).default([]),
  /** ISO timestamp of last write — useful for ops debugging. */
  updatedAt: z.string().datetime(),
});
export type FormPointer = z.infer<typeof FormPointerSchema>;

export const FORM_POINTER_VERSION = 1 as const;

export function formPointerKey(tenantSlug: string, formSlug: string): string {
  return `_forms/${tenantSlug}/${formSlug}.json`;
}
